import * as React from 'react'
import {
  DatePicker as AriaDatePicker,
  DatePickerStateContext,
  Group,
  Button as AriaButton,
  Popover,
  Dialog,
  DateInput as AriaDateInput,
  type DatePickerProps as AriaDatePickerProps,
  type DateValue,
} from 'react-aria-components'
import {
  today,
  getLocalTimeZone,
  CalendarDateTime,
} from '@internationalized/date'
import { CalendarBlank, X } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { DateSegment } from './date-field'
import { Calendar } from './calendar'
import { TimeColumns } from './time-columns'
import { Button } from './button'

/**
 * DatePicker — a segmented date input paired with a calendar dropdown, built on
 * react-aria-components. The input box reuses our `Input` look; the trigger
 * opens our white `Popover` containing the `Calendar`.
 *
 *   <DatePicker value={date} onChange={setDate} />
 *
 * Variants:
 *  - time `granularity` (`"hour"`/`"minute"`/`"second"`, with a `CalendarDateTime`
 *    value) adds inline time segments AND scrollable time columns beside the
 *    calendar (`minuteStep` sets the increment) — the time is pickable, not just
 *    typable. The popover stays open while you pick.
 *  - `withApply` adds an Apply / Cancel footer that *gates* the commit: editing
 *    the calendar/time edits a draft and only fires `onChange` on Apply; Cancel
 *    reverts.
 *  - `clearable` shows a trailing × (once a date is set) that resets the value to
 *    null — the same clear affordance as `SearchBar`.
 *
 * Boolean props follow DS naming (`disabled`/`readOnly`/`required`/`invalid`)
 * and map to react-aria's `is*`.
 */

type FieldSize = 'sm' | 'md' | 'lg'
type FieldStatus = 'default' | 'error' | 'success'

const groupSize: Record<FieldSize, string> = {
  sm: 'h-8 pl-2.5 pr-1 text-xs',
  md: 'h-9 pl-3 pr-1 text-sm',
  lg: 'h-10 pl-3.5 pr-1 text-base',
}

const groupBase =
  'inline-flex w-fit items-center gap-1 rounded-lg border bg-fbui-base-white text-fbui-gray-800 transition-colors focus-within:ring-4 data-[disabled]:cursor-not-allowed data-[disabled]:bg-fbui-gray-50 data-[disabled]:text-fbui-text-disabled'

const groupStatus = {
  default:
    'border-fbui-gray-300 focus-within:border-fbui-basil-700 focus-within:ring-fbui-ring-brand',
  error:
    'border-fbui-red-500 focus-within:border-fbui-red-600 focus-within:ring-fbui-ring-error',
  success:
    'border-fbui-green-500 focus-within:border-fbui-green-600 focus-within:ring-fbui-ring-success',
} as const

/** Time columns bound to the picker's value (the time part), via context. */
const DatePickerTimeColumns = ({
  hourCycle,
  minuteStep,
  size,
}: {
  hourCycle?: 12 | 24
  minuteStep?: number
  size: FieldSize
}) => {
  const state = React.useContext(DatePickerStateContext)
  if (!state) return null
  const value = state.value
  const hasTime = !!value && 'hour' in value

  const handleChange = (h: number, m: number) => {
    if (value && 'hour' in value) {
      state.setValue(value.set({ hour: h, minute: m }))
    } else if (value) {
      // A date without a time part — keep its date, attach the picked time.
      state.setValue(
        new CalendarDateTime(value.year, value.month, value.day, h, m)
      )
    } else {
      // Picking a time before any date — anchor to today.
      const base = today(getLocalTimeZone())
      state.setValue(
        new CalendarDateTime(base.year, base.month, base.day, h, m)
      )
    }
  }

  return (
    <div className="border-l border-fbui-gray-200 pl-3">
      <TimeColumns
        hour={hasTime ? value.hour : undefined}
        minute={hasTime ? value.minute : undefined}
        hourCycle={hourCycle}
        minuteStep={minuteStep}
        size={size}
        onChange={handleChange}
      />
    </div>
  )
}

export interface DatePickerProps extends Omit<
  AriaDatePickerProps<DateValue>,
  | 'isDisabled'
  | 'isReadOnly'
  | 'isRequired'
  | 'isInvalid'
  | 'className'
  | 'value'
  | 'defaultValue'
  | 'onChange'
  | 'isOpen'
  | 'defaultOpen'
  | 'onOpenChange'
  | 'shouldCloseOnSelect'
> {
  value?: DateValue | null
  defaultValue?: DateValue | null
  onChange?: (value: DateValue | null) => void
  size?: FieldSize
  status?: FieldStatus
  disabled?: boolean
  readOnly?: boolean
  required?: boolean
  invalid?: boolean
  /** Month + year dropdowns in the calendar header. On by default; set `false` for a plain label. */
  monthYearSelect?: boolean
  /** Minute increment for the popover time columns (date-time granularity). */
  minuteStep?: number
  /** Add an Apply / Cancel footer that gates the commit until Apply. */
  withApply?: boolean
  /** Show a trailing × (once a value is set) that clears the field to null. */
  clearable?: boolean
  className?: string
}

export const DatePicker = React.forwardRef<HTMLDivElement, DatePickerProps>(
  (
    {
      value,
      defaultValue,
      onChange,
      size = 'md',
      status,
      disabled,
      readOnly,
      required,
      invalid,
      monthYearSelect = true,
      minuteStep = 5,
      withApply = false,
      clearable = false,
      granularity,
      hourCycle,
      className,
      ...props
    },
    ref
  ) => {
    const resolved = invalid ? 'error' : (status ?? 'default')
    const iconSize =
      size === 'sm' ? 'size-6' : size === 'lg' ? 'size-8' : 'size-7'
    const showTime = granularity != null && granularity !== 'day'

    const isControlled = value !== undefined
    const [internal, setInternal] = React.useState<DateValue | null>(
      defaultValue ?? null
    )
    const committed = isControlled ? value : internal
    const [open, setOpen] = React.useState(false)
    const [draft, setDraft] = React.useState<DateValue | null>(
      committed ?? null
    )

    // While the popover is closed, keep the draft in step with the committed
    // value so an external (controlled) change isn't masked by a stale draft.
    React.useEffect(() => {
      if (withApply && !open) setDraft(committed ?? null)
    }, [committed, open, withApply])

    const activeValue = withApply ? draft : (committed ?? null)

    const commit = (next: DateValue | null) => {
      if (!isControlled) setInternal(next)
      onChange?.(next)
    }
    const handleAriaChange = (next: DateValue | null) => {
      if (withApply) setDraft(next)
      else commit(next)
    }
    const handleOpenChange = (next: boolean) => {
      if (next && withApply) setDraft(committed ?? null)
      setOpen(next)
    }
    const applyDraft = () => {
      commit(draft)
      setOpen(false)
    }
    const cancelDraft = () => {
      setDraft(committed ?? null)
      setOpen(false)
    }

    return (
      <AriaDatePicker
        ref={ref}
        value={activeValue}
        onChange={handleAriaChange}
        isOpen={open}
        onOpenChange={handleOpenChange}
        // Keep the popover open while picking time, or when gating with Apply.
        shouldCloseOnSelect={!(withApply || showTime)}
        granularity={granularity}
        hourCycle={hourCycle}
        isDisabled={disabled}
        isReadOnly={readOnly}
        isRequired={required}
        isInvalid={invalid}
        className={cn('flex flex-col gap-1.5', className)}
        {...props}
      >
        <Group
          className={cn(groupBase, groupSize[size], groupStatus[resolved])}
        >
          <AriaDateInput className="flex flex-1 items-center">
            {(segment) => <DateSegment segment={segment} />}
          </AriaDateInput>
          {clearable && !disabled && !readOnly && activeValue != null && (
            <button
              type="button"
              // Field-specific label ("Clear <field name>") so a range's two
              // clear buttons are distinguishable to screen readers; falls back
              // to "Clear date" when the picker has no aria-label.
              aria-label={
                props['aria-label']
                  ? `Clear ${props['aria-label']}`
                  : 'Clear date'
              }
              // Clear routes through the same change path as editing, so it
              // respects `withApply` (stages null) and controlled/uncontrolled.
              onClick={() => handleAriaChange(null)}
              className={cn(
                'inline-flex shrink-0 items-center justify-center rounded-md text-fbui-gray-500 outline-none transition-colors hover:bg-fbui-gray-100 hover:text-fbui-gray-600 focus-visible:ring-2 focus-visible:ring-fbui-ring-brand',
                iconSize
              )}
            >
              <X weight="bold" aria-hidden="true" className="size-3.5" />
            </button>
          )}
          <AriaButton
            aria-label="Open calendar"
            className={cn(
              'inline-flex shrink-0 items-center justify-center rounded-md text-fbui-gray-500 outline-none transition-colors data-[hovered]:bg-fbui-gray-100 data-[hovered]:text-fbui-gray-700 data-[focus-visible]:ring-2 data-[focus-visible]:ring-fbui-ring-brand data-[disabled]:text-fbui-gray-300',
              iconSize
            )}
          >
            {/* size-3.5 matches the clear × so the two trailing glyphs are the
                same size (also aligns with the Combobox caret). */}
            <CalendarBlank className="size-3.5" aria-hidden="true" />
          </AriaButton>
        </Group>
        <Popover
          placement="bottom start"
          offset={8}
          className="z-50 rounded-xl border border-fbui-gray-200 bg-fbui-base-white p-3 shadow-fbui-lg outline-none"
        >
          <Dialog aria-label="Choose date" className="outline-none">
            <div className={cn('flex', showTime && 'gap-3')}>
              <div className="flex flex-col gap-3">
                <Calendar monthYearSelect={monthYearSelect} />
                {withApply && (
                  <div className="flex justify-end gap-2 border-t border-fbui-gray-200 pt-3">
                    <Button
                      variant="primary"
                      appearance="outline"
                      size="sm"
                      onClick={cancelDraft}
                    >
                      Cancel
                    </Button>
                    <Button variant="primary" size="sm" onClick={applyDraft}>
                      Apply
                    </Button>
                  </div>
                )}
              </div>
              {showTime && (
                <DatePickerTimeColumns
                  hourCycle={hourCycle}
                  minuteStep={minuteStep}
                  size={size}
                />
              )}
            </div>
          </Dialog>
        </Popover>
      </AriaDatePicker>
    )
  }
)
DatePicker.displayName = 'DatePicker'
